/**
 * The signed-out brokerage directory (#1050): area membership, the card shape,
 * ordering and the no-account filters.
 *
 * Dependency-free on purpose (no Supabase client, no React): the same code runs
 * in the browser, in the Node prerender bundle (scripts/prerender.mjs builds the
 * directory from the profiles it already fetched) and in unit tests.
 *
 * EVERY VALUE COMES FROM get_public_organization_profile. A directory entry is
 * a projection of keys the anonymous allowlist already returns for that
 * organization's public page — slug, name, type, city, state, logo_url,
 * offices, work_model and listing_kind (#1039) — and nothing else. There is no
 * new public field here, and nothing reads `organizations` or `profile_data`.
 *
 * AREA MEMBERSHIP IS A CITY ALLOWLIST FOR NOW. There is no market or county
 * column yet, so "greater Sacramento" is the CA cities below (Sacramento,
 * Placer, El Dorado, Yolo and Sutter counties), matched against the
 * organization's city and its office cities. When #1036 lands a staff-set
 * `market` on organizations and the public RPCs return it, replace
 * `inGreaterSacramento` with a `market === "sacramento-metro"` check and keep
 * this list only for the per-city pages.
 */

import { publicCloudBadge } from "@/lib/publicOrganizationProfile";

export const DIRECTORY_AREA_SLUG = "sacramento";
export const DIRECTORY_AREA_NAME = "greater Sacramento";
export const DIRECTORY_BASE_PATH = `/brokerages/${DIRECTORY_AREA_SLUG}`;

/** Where the build writes the directory snapshot (scripts/prerender.mjs). */
export const DIRECTORY_SNAPSHOT_PATH = `${DIRECTORY_BASE_PATH}/directory.json`;
export const DIRECTORY_SNAPSHOT_VERSION = 1;

/**
 * Cities and unincorporated communities of greater Sacramento, by county.
 * Every entry is in California; a city of the same name in another state never
 * matches (see `inGreaterSacramento`).
 */
export const GREATER_SACRAMENTO_CITIES_BY_COUNTY: Record<string, readonly string[]> = {
  Sacramento: [
    "Sacramento", "Elk Grove", "Citrus Heights", "Folsom", "Rancho Cordova", "Galt", "Isleton",
    "Carmichael", "Fair Oaks", "Orangevale", "Antelope", "North Highlands", "Rio Linda", "Elverta",
    "Wilton", "Gold River", "Arden-Arcade", "Herald", "Mather", "McClellan Park", "Rancho Murieta",
    "Walnut Grove", "Courtland", "Sloughhouse",
  ],
  Placer: [
    "Roseville", "Rocklin", "Lincoln", "Auburn", "Loomis", "Granite Bay", "Colfax", "Newcastle",
    "Penryn", "Sheridan", "Meadow Vista", "Foresthill", "Applegate", "Tahoe City", "Kings Beach",
    "Olympic Valley", "Carnelian Bay", "Tahoe Vista",
  ],
  "El Dorado": [
    "Placerville", "El Dorado Hills", "Cameron Park", "Shingle Springs", "Diamond Springs",
    "South Lake Tahoe", "Pollock Pines", "Georgetown", "Cool", "Somerset", "Pilot Hill",
    "Garden Valley", "Rescue", "El Dorado", "Camino",
  ],
  Yolo: [
    "Davis", "Woodland", "West Sacramento", "Winters", "Esparto", "Knights Landing", "Clarksburg",
    "Dunnigan", "Yolo", "Capay",
  ],
  Sutter: ["Yuba City", "Live Oak", "Sutter", "Meridian", "Nicolaus", "Robbins", "Pleasant Grove"],
};

/** The main cities that get their own page at /brokerages/sacramento/:city. */
export const DIRECTORY_CITY_PAGES = [
  "Sacramento",
  "Roseville",
  "Rocklin",
  "Folsom",
  "Elk Grove",
  "Davis",
  "Auburn",
  "El Dorado Hills",
  "Lincoln",
  "Granite Bay",
  "Yuba City",
] as const;

/** "El Dorado Hills" → "el-dorado-hills". */
export function citySlug(city: string): string {
  return city
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The city page path for a main city. */
export function directoryCityPath(city: string): string {
  return `${DIRECTORY_BASE_PATH}/${citySlug(city)}`;
}

/** Every directory path that is prerendered and listed in the sitemap. */
export function directoryPaths(): string[] {
  return [DIRECTORY_BASE_PATH, ...DIRECTORY_CITY_PAGES.map(directoryCityPath)];
}

/** The main city a page slug names, or null for an unknown slug. */
export function cityForSlug(slug: string | undefined | null): string | null {
  if (!slug) return null;
  return DIRECTORY_CITY_PAGES.find((city) => citySlug(city) === slug) ?? null;
}

/** Case, accent, punctuation and spacing insensitive key for a city name. */
function cityKey(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** cityKey → canonical display name, for every allowlisted city. */
const AREA_CITY_BY_KEY: ReadonlyMap<string, string> = new Map(
  Object.values(GREATER_SACRAMENTO_CITIES_BY_COUNTY)
    .flat()
    .map((city) => [cityKey(city), city] as const),
);

function isCalifornia(state: unknown): boolean {
  if (typeof state !== "string") return false;
  const s = state.trim().toLowerCase();
  return s === "ca" || s === "california";
}

/** The canonical area city for a (city, state) pair, or null when outside the area. */
export function areaCity(city: unknown, state: unknown): string | null {
  if (typeof city !== "string" || !city.trim() || !isCalifornia(state)) return null;
  return AREA_CITY_BY_KEY.get(cityKey(city)) ?? null;
}

export type ListingKind = "owned" | "listed";

/**
 * One directory card. This is also the snapshot's row shape, so it uses the
 * RPC's snake_case names for the values copied from it.
 */
export interface DirectoryEntry {
  slug: string;
  name: string;
  type: string | null;
  city: string | null;
  state: string | null;
  logo_url: string | null;
  /** "listed" is a staff-set basic listing (#1039); everything else is a completed, owned profile. */
  listing_kind: ListingKind;
  /** The public cloud badge ("Cloud brokerage" / "Part of a cloud brokerage"), or null. */
  cloud_badge: string | null;
  /** Canonical area cities this organization is in: its own city first, then its offices'. */
  area_cities: string[];
}

/** The subset of a get_public_organization_profile payload the directory reads. */
export interface DirectoryProfileInput {
  slug?: unknown;
  name?: unknown;
  type?: unknown;
  city?: unknown;
  state?: unknown;
  logo_url?: unknown;
  offices?: unknown;
  work_model?: unknown;
  listing_kind?: unknown;
}

const str = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/**
 * The card for one public profile, or null when the payload is unusable. Area
 * membership is NOT decided here; see `inGreaterSacramento`.
 */
export function directoryEntryFromProfile(profile: DirectoryProfileInput | null | undefined): DirectoryEntry | null {
  if (!profile || typeof profile !== "object") return null;
  const slug = str(profile.slug);
  const name = str(profile.name);
  if (!slug || !name) return null;

  const city = str(profile.city);
  const state = str(profile.state);
  const areaCities: string[] = [];
  const add = (c: string | null) => {
    if (c && !areaCities.includes(c)) areaCities.push(c);
  };
  add(areaCity(city, state));
  if (Array.isArray(profile.offices)) {
    for (const office of profile.offices) {
      if (office && typeof office === "object") {
        const o = office as { city?: unknown; state?: unknown };
        add(areaCity(o.city, o.state));
      }
    }
  }

  return {
    slug,
    name,
    type: str(profile.type),
    city,
    state,
    logo_url: str(profile.logo_url),
    // Absent before #1039, when every public organization was an owned one.
    listing_kind: profile.listing_kind === "listed" ? "listed" : "owned",
    cloud_badge: publicCloudBadge(profile.work_model),
    area_cities: areaCities,
  };
}

/**
 * True when a profile would match an area city but has no state, so it is left
 * out (a same-named city elsewhere must never show). The prerender pass counts
 * these so a staff-created org missing its state is not dropped silently.
 */
export function droppedForMissingState(profile: DirectoryProfileInput | null | undefined): boolean {
  if (!profile) return false;
  const hasState = typeof profile.state === "string" && profile.state.trim() !== "";
  if (hasState) return false;
  return typeof profile.city === "string" && AREA_CITY_BY_KEY.has(cityKey(profile.city));
}

export function inGreaterSacramento(entry: DirectoryEntry): boolean {
  return entry.area_cities.length > 0;
}

/** Completed (owned) profiles first, basic listings after; by name within each. */
export function sortDirectory(entries: DirectoryEntry[]): DirectoryEntry[] {
  return [...entries].sort((a, b) => {
    if (a.listing_kind !== b.listing_kind) return a.listing_kind === "owned" ? -1 : 1;
    return a.name.localeCompare(b.name, "en", { sensitivity: "base" }) || a.slug.localeCompare(b.slug);
  });
}

/** The area's directory from a set of public profiles: in the area, deduplicated by slug, ordered. */
export function buildDirectory(profiles: Array<DirectoryProfileInput | null | undefined>): DirectoryEntry[] {
  const bySlug = new Map<string, DirectoryEntry>();
  for (const profile of profiles) {
    const entry = directoryEntryFromProfile(profile);
    if (entry && inGreaterSacramento(entry) && !bySlug.has(entry.slug)) bySlug.set(entry.slug, entry);
  }
  return sortDirectory([...bySlug.values()]);
}

/**
 * Validate one snapshot row back into an entry. A snapshot is our own build
 * output, but it is still read over the network, so nothing is trusted blindly.
 */
export function entryFromSnapshotRow(row: unknown): DirectoryEntry | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  const slug = str(r.slug);
  const name = str(r.name);
  if (!slug || !name) return null;
  const areaCities = Array.isArray(r.area_cities)
    ? r.area_cities.filter((c): c is string => typeof c === "string" && AREA_CITY_BY_KEY.has(cityKey(c)))
    : [];
  if (areaCities.length === 0) return null;
  return {
    slug,
    name,
    type: str(r.type),
    city: str(r.city),
    state: str(r.state),
    logo_url: str(r.logo_url),
    listing_kind: r.listing_kind === "listed" ? "listed" : "owned",
    cloud_badge: r.cloud_badge === "Cloud brokerage" || r.cloud_badge === "Part of a cloud brokerage" ? r.cloud_badge : null,
    area_cities: areaCities,
  };
}

export interface DirectorySnapshot {
  version: number;
  area: string;
  generated_at: string;
  entries: DirectoryEntry[];
}

export function directorySnapshot(entries: DirectoryEntry[], generatedAt = new Date()): DirectorySnapshot {
  return {
    version: DIRECTORY_SNAPSHOT_VERSION,
    area: DIRECTORY_AREA_SLUG,
    generated_at: generatedAt.toISOString(),
    entries,
  };
}

/** The entries of a snapshot, or null when it is not a snapshot this code wrote. */
export function parseDirectorySnapshot(value: unknown): DirectoryEntry[] | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (v.version !== DIRECTORY_SNAPSHOT_VERSION || v.area !== DIRECTORY_AREA_SLUG || !Array.isArray(v.entries)) return null;
  const entries: DirectoryEntry[] = [];
  for (const row of v.entries) {
    const entry = entryFromSnapshotRow(row);
    if (entry) entries.push(entry);
  }
  return sortDirectory(entries);
}

// ---------------------------------------------------------------------------
// Filters (no account needed)
// ---------------------------------------------------------------------------

export interface DirectoryFilters {
  /** A canonical area city, or "" for all. */
  city: string;
  /** An org type value, or "" for all. */
  type: string;
  cloudOnly: boolean;
  query: string;
}

export const EMPTY_FILTERS: DirectoryFilters = { city: "", type: "", cloudOnly: false, query: "" };

export function hasActiveFilters(filters: DirectoryFilters): boolean {
  return Boolean(filters.city || filters.type || filters.cloudOnly || filters.query.trim());
}

export function filterDirectory(entries: DirectoryEntry[], filters: DirectoryFilters): DirectoryEntry[] {
  const q = filters.query.trim().toLowerCase();
  return entries.filter(
    (e) =>
      (!filters.city || e.area_cities.includes(filters.city)) &&
      (!filters.type || e.type === filters.type) &&
      // Same rule as Explore's cloud filter (treatedAsCloud): an office of a
      // cloud company is not itself a cloud brokerage.
      (!filters.cloudOnly || e.cloud_badge === "Cloud brokerage") &&
      (!q || e.name.toLowerCase().includes(q)),
  );
}

/** The area cities present in the data, alphabetically. */
export function directoryCities(entries: DirectoryEntry[]): string[] {
  return [...new Set(entries.flatMap((e) => e.area_cities))].sort((a, b) => a.localeCompare(b));
}

/** The org types present in the data, alphabetically. */
export function directoryTypes(entries: DirectoryEntry[]): string[] {
  return [...new Set(entries.map((e) => e.type).filter((t): t is string => Boolean(t)))].sort();
}

/** The organization's public page, the card's link. */
export function organizationProfilePath(slug: string): string {
  return `/organization/${encodeURIComponent(slug)}`;
}

/** "Roseville, CA" — the card's location line. Falls back to the first area city. */
export function entryLocation(entry: DirectoryEntry): string {
  if (entry.city) return entry.state ? `${entry.city}, ${entry.state}` : entry.city;
  return entry.area_cities[0] ? `${entry.area_cities[0]}, CA` : "";
}
